import React, { useMemo } from 'react';
import { calculateImpact } from '../utils/impact.utils.js';
import { SUSTAINABILITY_DEV_MODE as DEV_MODE } from '../utils/url.utils';

// --- LCA TOGGLE ---
const SHOW_LCA_DATA = false;

// --- DEV TOGGLE ---

/**
 * ImpactDashboard Subcomponent (formerly ScoreHeader)
 * Displays the dynamic breakdown and the scientific unit on every line.
 */
const ImpactDashboard = ({ impact, lotData, t }) => {
  const unitLabel = t("impact.kg_co2");

  const formatLine = (val) => (
    <>
      {val.toFixed(6)} <span className="unit-label">{unitLabel}</span>
    </>
  );

  return (
    <div className="score-header">
      <div className="banner">
        <table>
        <thead></thead>
        <tbody>
        <tr>
          <td className="label-cell">Total Impact:</td>
          <td><p className="impact-value-container">
            <strong className="impact-number">{impact.total.toFixed(6)}</strong> <small><span className="impact-unit">{unitLabel}</span></small>
          </p></td>
        </tr>
        </tbody>
        </table>                        
      </div>

      <div className="scoreBoard">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead></thead>
        <tbody>
        <tr>
          <td className="label-cell">
            <p className="productScore label">Production</p>
          </td>
          <td className="value-cell">
            <p className="productScore"><strong>{formatLine(impact.production)}</strong></p>
          </td>
        </tr>
        {SHOW_LCA_DATA &&
         lotData.normalizedTreatment &&
         lotData.normalizedTreatment !== "false" &&
         lotData.normalizedTreatment !== "none" && (
          <tr>
            <td className="label-cell">
              <p className="treatmentScore label">{lotData.normalizedTreatment} Treatment</p>
            </td>
            <td className="value-cell">
              <p className="treatmentScore"><strong>{formatLine(impact.treatment)}</strong></p>
            </td>
          </tr>
        )}
        <tr>
          <td className="label-cell">
            <p className="transpoScore label">Transportation</p>
          </td>
          <td className="value-cell">
            <p className="transpoScore"><strong>{formatLine(impact.transportation)}</strong></p>
          </td>
        </tr>
        {SHOW_LCA_DATA && (
          <tr>
            <td className="label-cell">
              <p className="farmScore label">Farm / Origin</p>
            </td>
            <td className="value-cell">
              <p className="farmScore"><strong>{formatLine(impact.farm)}</strong></p>
            </td>
          </tr>
        )}
        {impact.abattoir > 0.00000 && (
          <tr>
            <td className="label-cell">
              <p className="abattoirScore label">Abattoir</p>
            </td>
            <td className="value-cell">
              <p className="abattoirScore"><strong>{formatLine(impact.abattoir)}</strong></p>
            </td>
          </tr>
        )}        
        </tbody>
        </table>        
      </div>      
      {/* <div className="scoreboard-footer" style={{ marginTop: '15px'}}>
        <p style={{ fontSize: '10px', color: '#888', fontStyle: 'italic', lineHeight: '1.4em', margin: 0 }}>
          * Transportation and Farm/Origin values pending primary-source telemetry&nbsp;integration.
        </p>
      </div> */}
    </div>
  );
};

/**
 * ProductionStory Subcomponent
 */
const ProductionStory = ({ t, impact, lotData }) => {
  const facilityName = lotData?.facility || 'ALLIED';
  const productionValue = (impact.production * 1000).toFixed(3);

  return (
    <div className="production-story">
      <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>{t ? t("sustainability.production.title") : "ALLIED PRODUCTION"}</h2>
      <p>{t ? t("sustainability.production.p1") : ""}</p>
      <p>
        {t ? t("sustainability.production.p2")
          .replace("{{facility}}", facilityName) : ""}
      </p>
    </div>
  );
};

/**
 * SupplyStory Subcomponent
 */
const SupplyStory = ({ t }) => {
  return (
    <div className="supply-story">
      <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>{t ? t("sustainability.supply.title") : "SUPPLY CHAIN IMPACTS"}</h2>
      <p>{t ? t("sustainability.supply.p1") : ""}</p>
      <p>{t ? t("sustainability.supply.p2") : ""}</p>
    </div>
  );
};

/**
 * TransportationStory Subcomponent
 */
const TransportationStory = ({ t }) => {
  return (
    <div className="transportation-story">
      <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>{t ? t("sustainability.transportation.title") : "TRANSPORTATION IMPACTS"}</h2>
      <p>{t ? t("sustainability.transportation.p1") : ""}</p>
    </div>
  );
};

/**
 * TreatmentStory Subcomponent
 */
const TreatmentStory = ({ t }) => {
  return (
    <div className="treatment-story">
      <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>{t ? t("sustainability.treatment.title") : "TREATMENT IMPACTS"}</h2>
      <p>{t ? t("sustainability.treatment.p1") : ""}</p>
    </div>
  );
};

/**
 * AbattoirStory Subcomponent
 */
const AbattoirStory = ({ t }) => {
  return (
    <div className="abattoir-story">
      <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>{t ? t("sustainability.abattoir.title") : "SLAUGHTERHOUSE / PRE-PROCESSING"}</h2>
      <p>{t ? t("sustainability.abattoir.p1") : ""}</p>
    </div>
  );
};

/**
 * OffsetStory Subcomponent (Legacy Fallback / POW CTA)
 */
const OffsetStory = ({ t }) => {
  return (
    <div className="offset-story">
      <p style={{ marginTop: '20px', textTransform: 'uppercase' }}>{t ? t("sustainability.offset_cta.title") : "HELP OFFSET YOUR PURCHASE"}</p>
      <p dangerouslySetInnerHTML={{ __html: t ? t("sustainability.offset_cta.p1") : "" }} />
      <p dangerouslySetInnerHTML={{ __html: t ? t("sustainability.offset_cta.p2") : "" }} />
      <p dangerouslySetInnerHTML={{ __html: t ? t("sustainability.offset_cta.p3") : "" }} />
    </div>
  );
};

/**
 * EnvironmentalImpactIntro Subcomponent
 */
const EnvironmentalImpactIntro = ({ t }) => {
  return (
    <div className="ei-intro">
      <p dangerouslySetInnerHTML={{ __html: t ? t("sustainability.intro.p1") : "" }}></p>      
    </div>
  );
};

/**
 * EIContent Subcomponent
 * Orchestrates the dashboard and stories.
 */
const EIContent = ({ impact, lotData, t, showImpact }) => {
  const hasTreatment = lotData?.normalizedTreatment && 
                       lotData.normalizedTreatment.toLowerCase() !== "none" && 
                       lotData.normalizedTreatment.toLowerCase() !== "false" &&
                       lotData.normalizedTreatment !== "";
  const hasAbattoir = impact.abattoir > 0.00000;

  if (!showImpact) {
    return <OffsetStory t={t} />;
  }

  return (
    <>              
      <ImpactDashboard impact={impact} lotData={lotData} t={t} />
      <EnvironmentalImpactIntro t={t} />
      {hasTreatment && <TreatmentStory t={t} />}
      <ProductionStory t={t} impact={impact} lotData={lotData} />      
      <TransportationStory t={t} />
      {hasAbattoir && <AbattoirStory t={t} />}
      <SupplyStory t={t} />
    </>
  );
};

/**
 * Main Sustainability Component
 */
const Sustainability = (props) => { 
  const { t, showImpact } = props;

  const impactData = useMemo(() => {
    if (props.impactData) {
        return props.impactData;
    }
    const result = calculateImpact(props.lotData);
    if (DEV_MODE) { console.log(`The Verified Impact for ${props.activeLot} is:`, result); }

    return result;
  }, [props.impactData, props.lotData.facility, props.lotData.origin, props.lotData.down_type, props.lotData.verified_fp, props.lotData.treatment, props.activeLot]);

  const title = t("environmentalImpact");
  
  return (
    <div className="content-ac su hide">
      <input className="ac-input top" id="ac-7afd" name="ac-7afd" data-slug="SUSTAINABILITY" type="checkbox" onChange={ () => props.changeWord('SUSTAINABILITY') } />
      <label className="ac-label" htmlFor="ac-7afd">{ title }</label>
      <article className="ac-text ac-7afd">
        <div className="single-post-interior" tabIndex="0">
          <EIContent impact={impactData} lotData={props.lotData} t={t} showImpact={showImpact} />
        </div>
      </article>
    </div>
  );   
}

export default Sustainability;
